import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {VideoControlsComponent} from '../video-controls/video-controls.component';

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [VideoControlsComponent],
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss']
})
export class VideoPlayerComponent implements OnChanges {
  @Input() src: string = '';
  @Input() autoplay: boolean = true;
  @Input() loop: boolean = false;
  @Input() controlsVisible: boolean = true;
  @Input() mobileControlsVisible: boolean = false;

  @Output() timeUpdate = new EventEmitter<number>();
  @Output() ended = new EventEmitter<void>();
  @Output() play = new EventEmitter<void>();
  @Output() pause = new EventEmitter<void>();
  @Output() loadedMetadata = new EventEmitter<{width: number, height: number}>();
  @Output() videoTouchStart = new EventEmitter<TouchEvent>();
  @Output() videoTouchEnd = new EventEmitter<TouchEvent>();
  @Output() controlsTouch = new EventEmitter<void>();

  @ViewChild('videoRef', {static: false}) videoRef?: ElementRef<HTMLVideoElement>;

  videoCurrentTime = signal(0);
  videoDuration = signal(0);
  videoVolume = signal(0.2); // Default to 20% volume
  isVideoMuted = signal(true); // Start as muted
  isVideoPaused = signal(true); // Start as paused
  showVideoControls = signal(true);

  get progressPercentage() {
    const duration = this.videoDuration();
    const currentTime = this.videoCurrentTime();
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle src changes to ensure video restarts properly
    if (changes['src'] && this.videoRef?.nativeElement) {
      const video = this.videoRef.nativeElement;

      // Reset video state when src changes
      this.videoCurrentTime.set(0);
      this.videoDuration.set(0);
      this.isVideoPaused.set(true);

      // Force video to load new source
      video.load();

      console.log('Video src changed, reloading:', {
        oldSrc: changes['src'].previousValue,
        newSrc: changes['src'].currentValue
      });
    }
  }

  ngAfterViewInit() {
    if (this.videoRef?.nativeElement) {
      this.videoRef.nativeElement.volume = this.videoVolume();
      this.videoRef.nativeElement.muted = this.isVideoMuted();
    }
  }

  onTimeUpdate(event: Event): void {
    const video = event.target as HTMLVideoElement;

    // Ensure video is not in a bad state
    if (video.readyState >= 1 && !isNaN(video.currentTime) && isFinite(video.currentTime)) {
      this.videoCurrentTime.set(video.currentTime);
      this.timeUpdate.emit(video.currentTime);
    }
  }

  onLoadedMetadata(event: Event): void {
    const video = event.target as HTMLVideoElement;
    this.videoDuration.set(video.duration);
    // Keep volume at 0.2 but ensure it starts muted
    video.volume = 0.2;
    video.muted = true;
    this.videoVolume.set(0.2);
    this.isVideoMuted.set(true);
    this.isVideoPaused.set(video.paused);

    // Reset currentTime to ensure proper display
    this.videoCurrentTime.set(0);

    console.log('Video metadata loaded:', {
      duration: video.duration,
      src: video.src,
      readyState: video.readyState
    });

    this.loadedMetadata.emit({width: video.videoWidth, height: video.videoHeight});
  }

  onPlay(event: Event): void {
    const video = event.target as HTMLVideoElement;
    console.log('Video play event', {currentTime: video.currentTime, duration: video.duration});
    this.isVideoPaused.set(false);
    this.play.emit();
  }

  onPause(event: Event): void {
    const video = event.target as HTMLVideoElement;
    console.log('Video pause event', {currentTime: video.currentTime, duration: video.duration});
    this.isVideoPaused.set(true);
    this.pause.emit();
  }

  onEnded(event: Event): void {
    console.log('Video ended');
    this.isVideoPaused.set(true);
    this.ended.emit();
  }

  onProgressClick(event: MouseEvent): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;

    // Get the actual progress track element for more accurate bounds
    const container = event.currentTarget as HTMLElement;
    const track = container.querySelector('.video-progress-track') as HTMLElement;

    if (track) {
      // Use the track bounds for more accurate calculation
      const rect = track.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      let percentage = clickX / rect.width;

      // Clamp percentage to valid range
      percentage = Math.max(0, Math.min(1, percentage));

      const newTime = percentage * video.duration;

      // Only update if the new time is valid
      if (!isNaN(newTime) && isFinite(newTime)) {
        video.currentTime = newTime;
        this.videoCurrentTime.set(newTime);
      }
    } else {
      // Fallback to container calculation
      const rect = container.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      let percentage = clickX / rect.width;

      // Clamp percentage to valid range
      percentage = Math.max(0, Math.min(1, percentage));

      const newTime = percentage * video.duration;

      // Only update if the new time is valid
      if (!isNaN(newTime) && isFinite(newTime)) {
        video.currentTime = newTime;
        this.videoCurrentTime.set(newTime);
      }
    }
  }

  onVolumeChange(volume: number): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    video.volume = volume;
    this.videoVolume.set(volume);
    this.isVideoMuted.set(volume === 0);
  }

  togglePlayPause(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => {
        this.isVideoPaused.set(false);
      }).catch((error) => {
        this.isVideoPaused.set(true);
      });
    } else {
      video.pause();
      this.isVideoPaused.set(true);
    }
  }

  toggleMute(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    video.muted = !video.muted;
    this.isVideoMuted.set(video.muted);
  }

  // Touch event handlers
  onVideoTouchStart(event: TouchEvent): void {
    this.videoTouchStart.emit(event);
  }

  onVideoTouchEnd(event: TouchEvent): void {
    this.videoTouchEnd.emit(event);
  }

  onControlsTouch(): void {
    this.controlsTouch.emit();
  }

  /**
   * Restart the video from the beginning
   */
  restartVideo(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;

    console.log('Restarting video from beginning', {
      currentTime: video.currentTime,
      duration: video.duration,
      paused: video.paused,
      ended: video.ended
    });

    // Reset to beginning
    video.currentTime = 0;
    this.videoCurrentTime.set(0);

    // Always try to play when restarting (for looping functionality)
    video.play().then(() => {
      this.isVideoPaused.set(false);
      console.log('Video restarted and playing successfully');
    }).catch(error => {
      console.error('Error restarting video:', error);
      // Try with muted fallback for autoplay restrictions
      if ((error as any).name === 'NotAllowedError') {
        console.log('Attempting to restart muted as fallback...');
        video.muted = true;
        this.isVideoMuted.set(true);

        video.play().then(() => {
          this.isVideoPaused.set(false);
          console.log('Video restarted successfully (muted)');
        }).catch(secondError => {
          console.error('Error restarting muted video:', secondError);
          this.isVideoPaused.set(true);
        });
      } else {
        this.isVideoPaused.set(true);
      }
    });
  }

  /**
   * Get the native video element (for external control if needed)
   */
  getVideoElement(): HTMLVideoElement | null {
    return this.videoRef?.nativeElement || null;
  }

  /**
   * Force sync video state with component state (for fixing display issues)
   */
  forceSyncState(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;

    console.log('Force syncing video state', {
      videoPaused: video.paused,
      componentPaused: this.isVideoPaused(),
      videoCurrentTime: video.currentTime,
      componentCurrentTime: this.videoCurrentTime(),
      videoDuration: video.duration,
      componentDuration: this.videoDuration(),
      videoVolume: video.volume,
      componentVolume: this.videoVolume()
    });

    // Sync all state
    this.isVideoPaused.set(video.paused);
    this.videoCurrentTime.set(video.currentTime);
    this.videoDuration.set(video.duration);
    this.videoVolume.set(video.volume);
    this.isVideoMuted.set(video.muted);
  }
}
